import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaUserPlus, FaGlobe } from 'react-icons/fa6';
import * as adminUsersService from '../../data/services/adminUsersService';
import { getAllOrgDomains } from '../../../../shared/utils/organizationDomainUtil';
import './AdminUsersScreen.css';

const AdminUsersScreen = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    confirmPassword: '',
    orgId: '',
  });
  const [orgDomains, setOrgDomains] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [newDomain, setNewDomain] = useState('');
  const [addingDomain, setAddingDomain] = useState(false);
  const [domainError, setDomainError] = useState(null);
  const [domainSuccess, setDomainSuccess] = useState(null);

  const loadOrganizations = useCallback(async () => {
    try {
      setLoadingOrgs(true);
      setErrors((prev) => ({ ...prev, org: null }));
      const orgs = await getAllOrgDomains();
      setOrgDomains(orgs);
    } catch (error) {
      console.error('Error loading organizations:', error);
      setErrors((prev) => ({ ...prev, org: 'Failed to load organizations' }));
    } finally {
      setLoadingOrgs(false);
    }
  }, []);

  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
    if (successMessage) setSuccessMessage(null);
  };

  const validate = () => {
    const newErrors = {};

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/[A-Z]/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one uppercase letter';
    } else if (!/[a-z]/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one lowercase letter';
    } else if (!/[0-9]/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one number';
    } else if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one special character';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.orgId) {
      newErrors.orgId = 'Organization is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setSubmitting(true);
    setSuccessMessage(null);
    try {
      await adminUsersService.createUser({
        email: formData.email.trim(),
        name: formData.name.trim() || null,
        password: formData.password,
        orgId: formData.orgId,
      });

      setSuccessMessage('User created successfully. They can log in immediately without email verification.');
      setFormData({
        email: '',
        name: '',
        password: '',
        confirmPassword: '',
        orgId: formData.orgId,
      });
    } catch (error) {
      setErrors({ submit: error.message || 'Failed to create user. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate('/admin/roles');
  };

  const handleAddDomain = async (e) => {
    e.preventDefault();
    const domain = newDomain.trim();
    if (!domain) {
      setDomainError('Enter a domain (e.g. example.com)');
      return;
    }
    setDomainError(null);
    setDomainSuccess(null);
    setAddingDomain(true);
    try {
      const created = await adminUsersService.createOrganization({ domain });
      await loadOrganizations();
      setFormData((prev) => ({ ...prev, orgId: created.orgId || created.id }));
      setNewDomain('');
      setDomainSuccess(`Domain "${created.domain || domain}" added. Organization ID: ${created.orgId || created.id}. You can now add users to it.`);
    } catch (error) {
      setDomainError(error.message || 'Failed to add domain.');
    } finally {
      setAddingDomain(false);
    }
  };

  const handleNewDomainChange = (value) => {
    setNewDomain(value);
    if (domainError) setDomainError(null);
    if (domainSuccess) setDomainSuccess(null);
  };

  return (
    <div className="admin-users-screen">
      <div className="admin-users-container">
        <header className="admin-users-header">
          <button
            className="back-button"
            onClick={handleBack}
            aria-label="Back to Admin"
          >
            <FaArrowLeft className="back-icon" />
          </button>
          <div className="admin-users-header-content">
            <h1 className="admin-users-title">
              <FaUserPlus className="title-icon" />
              Add User
            </h1>
            <p className="admin-users-subtitle">
              Create users in specific organizations without email verification. New users can log in immediately.
            </p>
          </div>
        </header>

        <form onSubmit={handleSubmit} className="admin-users-form">
          {successMessage && (
            <div className="success-message" role="alert">
              {successMessage}
            </div>
          )}
          {errors.submit && (
            <div className="error-banner" role="alert">
              {errors.submit}
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                Email <span className="required">*</span>
              </label>
              <input
                id="email"
                type="email"
                className={`form-input ${errors.email ? 'error' : ''}`}
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="user@example.com"
                disabled={submitting}
                autoComplete="email"
              />
              {errors.email && (
                <span className="field-error">{errors.email}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="name" className="form-label">
                Display Name
              </label>
              <input
                id="name"
                type="text"
                className="form-input"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Optional - defaults to email prefix"
                disabled={submitting}
                autoComplete="name"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="password" className="form-label">
                Password <span className="required">*</span>
              </label>
              <input
                id="password"
                type="password"
                className={`form-input ${errors.password ? 'error' : ''}`}
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                placeholder="Min 8 chars, upper, lower, number, special"
                disabled={submitting}
                autoComplete="new-password"
              />
              {errors.password && (
                <span className="field-error">{errors.password}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword" className="form-label">
                Confirm Password <span className="required">*</span>
              </label>
              <input
                id="confirmPassword"
                type="password"
                className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
                value={formData.confirmPassword}
                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                placeholder="Re-enter password"
                disabled={submitting}
                autoComplete="new-password"
              />
              {errors.confirmPassword && (
                <span className="field-error">{errors.confirmPassword}</span>
              )}
            </div>
          </div>

          <div className="form-group add-domain-section">
            <label className="form-label">
              <FaGlobe className="add-domain-icon" /> Add new domain
            </label>
            <p className="add-domain-hint">Add a domain to create a new organization (org ID is auto-generated, same as on signup).</p>
            <div className="add-domain-row">
              <input
                type="text"
                className={`form-input ${domainError ? 'error' : ''}`}
                value={newDomain}
                onChange={(e) => handleNewDomainChange(e.target.value)}
                placeholder="example.com"
                disabled={addingDomain}
                aria-label="New domain"
              />
              <button
                type="button"
                className="add-domain-button"
                onClick={handleAddDomain}
                disabled={addingDomain || !newDomain.trim()}
              >
                {addingDomain ? 'Adding...' : 'Add domain'}
              </button>
            </div>
            {domainError && <span className="field-error">{domainError}</span>}
            {domainSuccess && <span className="field-success">{domainSuccess}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="orgId" className="form-label">
              Organization <span className="required">*</span>
            </label>
            <select
              id="orgId"
              className={`form-input ${errors.orgId ? 'error' : ''}`}
              value={formData.orgId}
              onChange={(e) => handleInputChange('orgId', e.target.value)}
              disabled={loadingOrgs || submitting}
              style={{ cursor: loadingOrgs ? 'wait' : 'pointer' }}
            >
              <option value="">Select an organization</option>
              {orgDomains.map((org) => (
                <option key={org.orgId} value={org.orgId}>
                  {org.domain || org.orgId}
                </option>
              ))}
            </select>
            {orgDomains.length === 0 && !loadingOrgs && (
              <span className="field-error">No organizations found. Add a domain above to create one.</span>
            )}
            {errors.orgId && (
              <span className="field-error">{errors.orgId}</span>
            )}
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="cancel-button"
              onClick={handleBack}
              disabled={submitting}
            >
              Back
            </button>
            <button
              type="submit"
              className="submit-button"
              disabled={submitting || loadingOrgs}
            >
              {submitting ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminUsersScreen;
